<script lang="ts" setup>
//External
import { useRoute } from 'vue-router';
import { PaintBrushIcon,TableCellsIcon,UserPlusIcon,ChevronDownIcon,PlusIcon } from '@heroicons/vue/24/solid';
import { h,markRaw, ref, onMounted, defineProps, computed, watch,inject } from 'vue';
import type { Component, Ref } from 'vue';
import type { FunctionalComponent, HTMLAttributes, VNodeProps } from 'vue';
import { useCookies } from '@vueuse/integrations/useCookies'
import axios from 'axios';
import { CaretSortIcon } from '@radix-icons/vue'
import { createReusableTemplate, useMediaQuery } from '@vueuse/core'
//UI
import { Button } from '@/components/ui/button'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import { Drawer, DrawerContent, DrawerTrigger } from '@/components/ui/drawer'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
//NewSpark
import  ugcMenu from '@/data/menu';
import  mmMenu from '@/data/mmmenu';
import { logout} from '@/services/Auth'
import permissions from "@/services/permissions";
import { Member  } from '@/types/Member'
import { VhostInfo,VhostSettings  } from '@/types/Vhost'
const { checkPermission  } = permissions();
const userInfo = inject<Ref<Member> | null>('userInfo', null);
const vhostId = inject<Ref<string> | null>('vhostId', null);

const cookies = useCookies(['collapsed','SABRE_ID']);
const props = defineProps({
	theme: {
		type: String, // Use `String` instead of `string`
		required: false,
		default: 'light', // Default value
	},
});


interface MenuItem {
	label: string;
	link?: string;
	icon: Component; 
	children?: MenuItem[];
}
const menuItems = ref<MenuItem[]>([]);
const route = useRoute(); 
const currentRoute = computed(() => route.path);

const openDropdowns = ref(new Set());
const popupOpen = ref(false);
const commandOpen = ref(false);
const isCollapsed = ref(false);
const displayProjects = ref(false);
const projects = ref<VhostInfo[]>([]);
const searchQuery = ref('');  // This will hold the search query from the user
const isDesktop = useMediaQuery('(min-width: 768px)');
const selectedProject = ref<VhostInfo | null>(null);

const toggleDropdown =  (label: string) => {
	if (openDropdowns.value.has(label)) openDropdowns.value.delete(label);
	else openDropdowns.value.add(label);
};
const toggleCollapse = () => {
	isCollapsed.value = !isCollapsed.value;
	cookies.set('collapsed',  isCollapsed.value, { maxAge: 3600 });
};

// Fetch projects from the API
const togglePopup = () => {
	popupOpen.value = !popupOpen.value;
};


const viewProfile = () => {
	console.log("Viewing profile...");
};

const goToSettings = () => {
	console.log("Going to settings...");
};
const callLogout = async () => {
	await logout();
	window.location.href = "/";
};

const mmMode = inject<Ref<boolean>>('mmMode', ref(false))

// Determine which menu to show based on mmMode
const loadMenu = () => {
	if (mmMode.value) {
		menuItems.value = mmMenu
	} else {
		menuItems.value = ugcMenu
	}
}

// Set up onMounted hook to fetch projects
onMounted(async () => {
	const cookieCollasped = cookies.get('collapsed');
	if ( cookieCollasped) {
		isCollapsed.value = cookieCollasped;
	}
	loadMenu()
});

// Watch mmMode changes (e.g., from header toggle) and swap menu
watch(mmMode, () => {
	loadMenu()
});

// Watch for changes in searchQuery
watch(searchQuery, (newQuery) => {
	console.log('Search query updated:', newQuery); // Debugging step
});
const isOpen = (label: string) => {
	// your logic to determine if the dropdown for the given label is open
	return openDropdowns.value.has(label); // Example check (if you're using a Set)
};
// Handle project selection
function onProjectSelect(project: VhostInfo) {
	selectedProject.value = project
	commandOpen.value = false;

	// Set the 'vid' cookie with the selected project's id
	cookies.set('vid', project.id, { maxAge: 30 });
	window.location.href = '/v7'; 
}       
function updateSearchQuery(event: InputEvent) {
	searchQuery.value = (event.target as HTMLInputElement).value;
}

// Reusable template for project list
const [UseTemplate, ProjectList] = createReusableTemplate();
</script>

<template>
	<aside class="h-screen bg-sidebar-background text-sidebar-foreground flex flex-col  border-r border-sidebar-border " :class="{ 'w-16': isCollapsed, 'w-[230px]': !isCollapsed, 'mm-mode-sidebar': mmMode }" >
		<!-- Toggle Button to Collapse/Expand -->
		<div class="text-xl font-semibold  cursor-pointer" @click="toggleCollapse" >
			<div class="flex items-center justify-center">
				<span v-if="isCollapsed" class="text-foreground">
					<img src="https://assets.newspark.ca/2/v7/gfx/newspark2025.png" class="w-20 m-auto">
				</span> <!-- Icon for collapsed state -->
				<span v-else class="text-foreground p-8">
					<img v-if="props.theme=='dark' || mmMode" src="https://assets.newspark.ca/2/gfx/nsio-dark.png" class="w-full aspect-auto">
					<img v-else src="https://v7.newspark.ca/a/img/mediafactory/newsparkblue.png" class="w-full aspect-auto">
				</span> <!-- Label for expanded state -->
			</div>
		</div>
		<div class="p-2" v-if="!isCollapsed && displayProjects">

			<!-- Use template for Command (Searchable Dropdown) -->
			<!-- Popover for desktop view -->
			<Popover v-if="isDesktop" v-model:open="commandOpen">
			<PopoverTrigger as-child>
			<Button variant="outline" class="w-full justify-start bg-sidebar-background text-sidebar-foreground border-border">
				{{ selectedProject ? selectedProject.name : "+ Set project" }}
			</Button>
			</PopoverTrigger>
			<PopoverContent class="w-full p-0" align="start">
			<ProjectList />
			</PopoverContent>
			</Popover>

			<!-- Drawer for mobile view -->
			<Drawer v-else v-model:open="commandOpen">
			<DrawerTrigger as-child>
			<Button variant="outline" class="w-full justify-start">
				{{ selectedProject ? selectedProject.name : "+ Set project" }}
			</Button>
			</DrawerTrigger>
			<DrawerContent>
			<div class="mt-4 border-t">
				<ProjectList />
			</div>
			</DrawerContent>
			</Drawer>
		</div>
		<nav class="flex-1 overflow-y-auto pl-3 ">
			<ul>
				<li v-for="item in menuItems" :key="item.label" class="group">
					<div class="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-sidebar-accent hover:text-sidebar-accent-foreground" @click="toggleDropdown(item.label)" :class="{ 'bg-primary text-primary-foreground': item.link && (route.path!='/') && route.path.startsWith(item.link), 'text-sidebar-foreground': true, 'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground': true }">
						<div class="flex items-center" v-if="item.link">
							<RouterLink :to="item.link">
							<div class="flex justify-start items-center">
								<div>
									<component :is="item.icon" class="w-5 h-5 " />
								</div>
								<div class="pl-2">
									<span class="font-extralight" v-if="!isCollapsed">{{ item.label }}</span> <!-- Only show label if expanded -->
								</div>
							</div>
							</RouterLink>
						</div>
						<div v-if="item.children" class="flex items-center justify-between w-full">
							<div class="flex items-center">
								<component :is="item.icon" class="w-5 h-5" />
								<span class="font-extralight pl-2" v-if="!isCollapsed">{{ item.label }}</span>
							</div>
							<ChevronDownIcon v-if="!isCollapsed" :class="[ 'w-4 h-4 transition-transform text-sidebar-foreground', isOpen(item.label) ? 'rotate-180' : 'rotate-0' ]" />
						</div>
					</div>
					<ul v-if="item.children" :class="[ 'overflow-hidden transition-all duration-300', isOpen(item.label) ? 'max-h-screen' : 'max-h-0' ]" >
						<li v-for="child in item.children" :key="child.label" class="pl-12 py-2 hover:bg-sidebar-accent  hover:text-sidebar-accent-foreground  cursor-pointer" :class="{ 'bg-primary text-primary-foreground': child.link && (route.path!='/') && route.path.startsWith(child.link), 'text-sidebar-foreground': true, 'hover:bg-sidebar-accent  hover:text-sidebar-accent-foreground': true }" >
							<RouterLink :to="child.link" v-if="child.link">
							<div class="flex justify-start items-center">
								<div>

									<component :is="child.icon" class="w-5 h-5 " />
								</div>
							<div class="pl-2">
								<span class="font-extralight" v-if="!isCollapsed">{{ child.label }}</span> <!-- Only show label if expanded -->
							</div>
							</div>
							</RouterLink >
						</li>
					</ul>
				</li>
			</ul>
		</nav>
		<div class="border-t border-sidebar-border  p-4 sticky bottom-0 text-primary-foreground" :class="mmMode ? 'bg-sidebar-accent' : 'bg-primary'" v-if="userInfo!=null">
			<div class="flex items-center mb-4 cursor-pointer" @click="togglePopup">
				<img :src="'/services/useravatar/'+userInfo.id+'/sm'" alt="User Avatar" class="w-10 aspect-square rounded-full mr-3" />
				<div>
					<div class="font-semibold opacity-100">{{ !isCollapsed ? userInfo.firstname+' '+userInfo.lastname : '' }}</div>
					<div class="text-sm text-sidebar-background opacity-100">{{ !isCollapsed ? userInfo.email : '' }}</div>
				</div>
			</div>

			<!-- Popup Menu -->
			<div v-if="popupOpen" class="absolute bottom-0 left-0 w-full bg-sidebar-accent hover:text-sidebar-accent-foreground  border-t border-sidebar-border mt-4 py-2 rounded-t-lg shadow-lg z-50" >
				<ul>
					<li class="px-4 py-2 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground cursor-pointer">
						<RouterLink to="/profile">Account</RouterLink>
					</li>
					<li class="px-4 py-2 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground cursor-pointer">
						<RouterLink to="/credentials">Credentials</RouterLink>
					</li>
					<li @click.prevent="callLogout" class="px-4 py-2 text-red-600 hover:bg-sidebar-accent  hover:text-sidebar-accent-foreground cursor-pointer">Logout</li>
				</ul>
			</div>
		</div>
	</aside>

</template>

